import React, { useEffect, useRef } from 'react';

import { Box } from '@rbilabs/universal-components';

import { ILocation } from '@rbi-ctg/frontend';
import useMap from 'hooks/use-map';
import { CenterPoint, MarkerTypes } from 'hooks/use-map/types';
import { useGeolocation } from 'state/geolocation';
import noop from 'utils/noop';

interface IStoreLocationMap {
  storeLocation: ILocation;
  title?: string;
  markerType?: MarkerTypes;
  showUserMarker?: boolean;
  isStaticMap?: boolean;
}

export const StoreLocationMap: React.FC<React.PropsWithChildren<IStoreLocationMap>> = ({
  storeLocation,
  title,
  markerType,
  showUserMarker = false,
  isStaticMap = false,
}) => {
  const { isPermissionGranted, userCoordinates } = useGeolocation();
  const markersCreated = useRef(false);
  const timerRef = useRef<NodeJS.Timeout>();

  const handleDragEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const coordinates = [storeLocation];
    if (isPermissionGranted && userCoordinates && showUserMarker) {
      coordinates.push(userCoordinates);
    }

    timerRef.current = setTimeout(() => {
      fitAndCenterFromCoords(coordinates as CenterPoint[]);
    }, 3000);
  };

  const { map, createMarker, fitAndCenterFromCoords } = useMap({
    position: storeLocation as CenterPoint,
    isStaticMap,
    eventListeners: {
      dragend: handleDragEnd,
    },
  });

  useEffect(() => {
    if (markersCreated.current) {
      return;
    }

    createMarker({
      id: 'store',
      type: markerType ?? MarkerTypes.StoreFocused,
      location: storeLocation,
      onPress: noop,
      title,
    });

    if (isPermissionGranted && userCoordinates && showUserMarker) {
      createMarker({
        id: 'user',
        type: MarkerTypes.User,
        location: userCoordinates,
        onPress: noop,
        title,
      });

      fitAndCenterFromCoords([storeLocation, userCoordinates]);
    }
  }, [
    createMarker,
    fitAndCenterFromCoords,
    isPermissionGranted,
    markerType,
    storeLocation,
    userCoordinates,
    title,
    showUserMarker,
  ]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <Box flex="1" pointerEvents={isStaticMap ? 'none' : 'auto'}>
      {map}
    </Box>
  );
};
